export type DeployedServiceType =
  | 'api-nest'
  | 'api-fhir-service'
  | 'saml-jackson'
  | 'supertokens-core'
  | 'hasura'
  | 'web-vite'
  | 'eluve-extension';

export type Environment =
  | 'production'
  | 'staging'
  | 'eluve-sargis'
  | 'production-siloam'
  | 'all-productions';

export type MonitorContext = 'service' | 'system' | 'application' | 'extension';

/**
 * GCP project ids that scope production backend logs in Datadog (the
 * `project_id:` tag). Single source for monitor queries (pulumi-core) and
 * the unknown-error digest (eluve-team-ops).
 */
export const PRODUCTION_PROJECT_IDS: Record<
  'production' | 'production-siloam',
  string
> = {
  production: 'pulumi-production-399505',
  'production-siloam': 'pulumi-production-siloam',
};

export type LogStatus = 'error' | 'warn';

export type EvaluationWindow = `${number}${'m' | 'h'}`;

/**
 * Notion "Monitor Runbooks" index page. Each runbook entry lives as a
 * toggle heading on this page; the fragment passed to `runbookLink()` is
 * the Notion block ID of that toggle. Block IDs are stable across text
 * edits but are regenerated if the block is deleted and re-created.
 */
const MONITOR_RUNBOOKS_URL =
  'https://www.notion.so/eluveinc/Monitor-Runbooks-3453a7e026af813c8051ff4daa7ac272';

/**
 * Build a deep-link into a specific runbook entry. Get the anchor via
 * Notion's "Copy link to block" on the toggle heading.
 */
export const runbookLink = (anchor: string): string =>
  `${MONITOR_RUNBOOKS_URL}#${anchor}`;

/**
 * Fallback Notion runbook link used for existing monitors that don't yet
 * have a dedicated runbook entry. All new monitors must supply a specific
 * `notionLink` via `runbookLink(anchor)` — see ENG-3722.
 */
export const DEFAULT_RUNBOOK_LINK = MONITOR_RUNBOOKS_URL;

type LogBasedErrorBase = {
  status?: LogStatus;
  threshold: number;
  priority: number;
  evaluationWindow?: EvaluationWindow;
  notionLink: string;
};

export type LogBasedError =
  | (LogBasedErrorBase & {
      messageContains: string;
      jsonContains?: Record<string, string>;
      title?: string;
    })
  | (LogBasedErrorBase & {
      messageContains?: never;
      jsonContains: Record<string, string>;
      title: string;
    });

export type TriageError = LogBasedError & {
  ticket: `ELU-${string}` | `ENG-${string}`;
};
